/**
 * Cart Context
 *
 * Manages cart operations including adding items and showing feedback messages.
 * Handles integration with WooCommerce Store API.
 *
 * @module contexts/cart
 */

import { StoreAPI } from '../../../api/store-api';
import { log } from '../../../utils/logging';
import { __ } from '@wordpress/i18n';
import type { SelectionContext } from './selection.svelte';
import type { ProductDataContext } from './product-data.svelte';
import type { PricingContext } from './pricing.svelte';

// Declare global window types for WooCommerce
declare global {
	interface Window {
		wpc_params: {
			store_api_url: string;
			store_api_nonce: string;
			checkout_url: string;
			ajax_url: string;
			nonce: string;
			is_quote_mode: boolean;
			cart_count: number;
		};
		jQuery?: {
			(selector: any): {
				trigger: (event: string) => void;
			};
		};
	}
}

export type MessageType = 'success' | 'error' | 'info';

/**
 * Creates reactive cart operations state
 *
 * Responsibilities:
 * - Add items to cart via WooCommerce Store API
 * - Show success/error messages with multi-language support
 * - Support multi-color arrays for balloon products
 * - Auto-clear messages after timeout
 * - Trigger cart refresh for WooCommerce
 *
 * @param selectionContext - The selection context for accessing variation and quantity
 * @param productDataContext - The product data context for accessing language
 * @returns Cart context with state and methods
 */
export function createCartContext(
	selectionContext: SelectionContext,
	productDataContext: ProductDataContext,
	pricingContext: PricingContext
) {
	// State
	let adding = $state(false);
	let message = $state('');
	let messageType = $state<MessageType>('info');

	// Message timeout reference
	let messageTimeout: number | undefined;

	/**
	 * Show a cart message with auto-clear
	 *
	 * @param text - The message text to display
	 * @param type - The message type (success, error, info)
	 * @param duration - How long to show the message in ms (default: 5000)
	 */
	function showMessage(text: string, type: MessageType, duration = 5000) {
		message = text;
		messageType = type;

		// Clear existing timeout
		if (messageTimeout) {
			clearTimeout(messageTimeout);
		}

		// Set new timeout to clear message
		messageTimeout = setTimeout(() => {
			message = '';
		}, duration) as unknown as number;
	}

	/**
	 * Clear the current message immediately
	 */
	function clearMessage() {
		message = '';
		if (messageTimeout) {
			clearTimeout(messageTimeout);
			messageTimeout = undefined;
		}
	}

	/**
	 * Build variation attributes array for Store API from all available sources
	 */
	function buildVariationAttrs(variation: NonNullable<SelectionContext['currentVariation']>): { attribute: string; value: string }[] {
		const allAttrNames = productDataContext.attributeNames;
		const configData = productDataContext.configData;
		const attrs: { attribute: string; value: string }[] = [];
		for (const key of allAttrNames) {
			let attrValue = (variation.attributes[key] as string) || '';
			if (!attrValue) {
				attrValue = selectionContext.selections[key] || '';
			}
			if (!attrValue) {
				const options = selectionContext.getAvailableOptions(key);
				if (options.length >= 1) {
					attrValue = options[0];
				}
			}
			if (!attrValue && configData?.terms?.[key]?.length) {
				attrValue = configData.terms[key][0].slug;
			}
			if (attrValue) {
				attrs.push({ attribute: key, value: attrValue });
			}
		}
		return attrs;
	}

	// Track quote mode locally so it updates after AJAX calls
	let isQuoteMode = $state(!!window.wpc_params.is_quote_mode);
	let cartCount = $state(window.wpc_params.cart_count || 0);

	/**
	 * Set or clear quote mode via AJAX, optionally clearing the cart.
	 */
	async function setQuoteMode(enabled: boolean, clearCart: boolean): Promise<void> {
		const body = new FormData();
		body.append('action', 'wpc_set_quote_mode');
		body.append('nonce', window.wpc_params.nonce);
		body.append('enabled', enabled ? '1' : '0');
		body.append('clear_cart', clearCart ? '1' : '0');

		await fetch(window.wpc_params.ajax_url, { method: 'POST', body });

		isQuoteMode = enabled;
		if (clearCart) cartCount = 0;

		// Stop or restart the PHP-rendered MutationObserver and update drawer button
		const wd = (window as any)._wpcQuoteDrawer;
		if (wd) {
			wd.active = enabled;
			if (!enabled && wd.observer) {
				wd.observer.disconnect();
			}
		}
		const drawerBtn = document.querySelector('.cpops-btn--checkout') as HTMLElement | null;
		if (drawerBtn) {
			drawerBtn.textContent = enabled
				? __('Offerte aanvragen', 'wpc')
				: __('Afrekenen', 'wpc');
		}
	}

	/**
	 * Build extensions object for Store API add-to-cart calls.
	 */
	function buildExtensions() {
		const isNonPrint = productDataContext.isNonPrintProduct;
		return {
			wpc: {
				spoed_option: selectionContext.deliveryOption,
				...( ! isNonPrint && { digitale_proef: selectionContext.digitaleProef } ),
				levertijd_dagen: pricingContext.deliveryDays,
				...(productDataContext.isBalloonProduct && selectionContext.selectedColors.length > 0 && {
					selected_colors: selectionContext.selectedColors.join(','),
				}),
			},
		};
	}

	/**
	 * Add current selection to cart
	 *
	 * If quote mode is active and the cart has items, asks the user to confirm
	 * clearing the cart before adding. Silently switches mode if cart is empty.
	 */
	async function addToCart(): Promise<void> {
		const variation = selectionContext.currentVariation;
		const quantity = selectionContext.quantity;
		const isComplete = selectionContext.isComplete;

		if (!isComplete) {
			showMessage(__('Voltooi alle selecties a.u.b.', 'wpc'), 'error');
			return;
		}

		if (!variation) {
			showMessage(__('Geen overeenkomende variatie gevonden.', 'wpc'), 'error');
			return;
		}

		try {
			adding = true;
			clearMessage();

			// If quote mode is active, we need to switch to order mode
			if (isQuoteMode) {
				if (cartCount > 0) {
					const ok = confirm(
						__('U heeft al producten in uw winkelwagen voor een offerte. Winkelwagen legen en doorgaan met bestellen?', 'wpc')
					);
					if (!ok) {
						adding = false;
						return;
					}
					await setQuoteMode(false, true);
				} else {
					await setQuoteMode(false, false);
				}
			}

			const storeApi = new StoreAPI(
				window.wpc_params.store_api_url,
				window.wpc_params.store_api_nonce
			);

			const variationAttrs = buildVariationAttrs(variation);
			await storeApi.addSingleItemToCart(variation.id, quantity, variationAttrs, buildExtensions());

			cartCount += quantity;
			showMessage(__('Product toegevoegd aan winkelwagen!', 'wpc'), 'success');

			// Refresh WooCommerce fragments + notify CartPops drawer
			if (window.jQuery) {
				window.jQuery(document.body).trigger('wc_fragment_refresh');
				window.jQuery(document.body).trigger('added_to_cart');
			}
			document.body.dispatchEvent(new Event('wc-blocks_added_to_cart'));

			log('Product added to cart:', 'log', {
				variationId: variation.id,
				quantity,
			});
		} catch (err) {
			log('Failed to add to cart:', 'error', err);
			showMessage(__('Toevoegen aan winkelwagen mislukt. Probeer opnieuw.', 'wpc'), 'error');
		} finally {
			adding = false;
		}
	}

	/**
	 * Request a quote for current selection
	 *
	 * If the cart has order items (not in quote mode), asks the user to confirm
	 * clearing the cart. Sets quote mode, adds item, then redirects to checkout.
	 */
	async function requestQuote(): Promise<void> {
		const variation = selectionContext.currentVariation;
		const quantity = selectionContext.quantity;
		const isComplete = selectionContext.isComplete;

		if (!isComplete) {
			showMessage(__('Voltooi alle selecties a.u.b.', 'wpc'), 'error');
			return;
		}

		if (!variation) {
			showMessage(__('Geen overeenkomende variatie gevonden.', 'wpc'), 'error');
			return;
		}

		try {
			adding = true;
			clearMessage();

			// If not in quote mode and cart has items, ask to clear
			if (!isQuoteMode) {
				if (cartCount > 0) {
					const ok = confirm(
						__('U heeft al producten in uw winkelwagen voor een bestelling. Winkelwagen legen en doorgaan met offerte?', 'wpc')
					);
					if (!ok) {
						adding = false;
						return;
					}
					await setQuoteMode(true, true);
				} else {
					await setQuoteMode(true, false);
				}
			}

			const storeApi = new StoreAPI(
				window.wpc_params.store_api_url,
				window.wpc_params.store_api_nonce
			);

			const variationAttrs = buildVariationAttrs(variation);
			await storeApi.addSingleItemToCart(variation.id, quantity, variationAttrs, buildExtensions());

			log('Product added to cart for quote:', 'log', {
				variationId: variation.id,
				quantity,
			});

			showMessage(__('Doorsturen naar offerte aanvraag...', 'wpc'), 'info');

			const checkoutUrl = window.wpc_params.checkout_url || '/checkout/';
			const quoteUrl = `${checkoutUrl}${checkoutUrl.includes('?') ? '&' : '?'}offerte=aanvraag`;
			window.location.href = quoteUrl;
		} catch (err) {
			log('Failed to request quote:', 'error', err);
			showMessage(__('Offerte aanvraag mislukt. Probeer opnieuw.', 'wpc'), 'error');
			adding = false;
		}
	}

	return {
		// State
		get adding() {
			return adding;
		},
		get message() {
			return message;
		},
		get messageType() {
			return messageType;
		},

		// Methods
		showMessage,
		clearMessage,
		addToCart,
		requestQuote,
	};
}

/**
 * Type for cart context
 */
export type CartContext = ReturnType<typeof createCartContext>;
